import React, { useState, useEffect } from 'react';

interface BaseCreditLimitFormData {
  base_credit_limit: string;
  base_credit_limit_desc: string;
}

interface BaseCreditLimitModalProps {
  isOpen: boolean;
  organization: {
    base_credit_limit?: number | null;
    base_credit_limit_desc?: string | null;
  };
  onClose: () => void;
  onSubmit: (formData: BaseCreditLimitFormData) => Promise<void>;
}

const BaseCreditLimitModal: React.FC<BaseCreditLimitModalProps> = ({
  isOpen,
  organization,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<BaseCreditLimitFormData>({
    base_credit_limit: '',
    base_credit_limit_desc: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when organization changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        base_credit_limit: organization.base_credit_limit?.toString() || '',
        base_credit_limit_desc: organization.base_credit_limit_desc || ''
      });
      setError('');
    }
  }, [isOpen, organization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validate that base_credit_limit is a valid number if provided
      if (formData.base_credit_limit && isNaN(Number(formData.base_credit_limit))) {
        throw new Error('Credit limit must be a valid number');
      }
      
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update base credit limit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof BaseCreditLimitFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            Edit Base Credit Limit
          </h3>
          
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Credit Limit
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.base_credit_limit}
                  onChange={(e) => handleInputChange('base_credit_limit', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 100000.00"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.base_credit_limit_desc}
                  onChange={(e) => handleInputChange('base_credit_limit_desc', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a description for the base credit limit..."
                  rows={4}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BaseCreditLimitModal;

